import dayjs from 'dayjs';
import { action, makeAutoObservable, observable, toJS } from 'mobx';
import { CardType, isCoexistableCard } from '../../components/TimetableVersion/graphql/types';
import {
  _ClassesQuery,
  _PeopleQuery,
  _RoomsQuery,
  _SubjectsQuery,
  ClassGridCard,
  SetCardRoomsDocument,
  SetTimetableCardsLockedDocument,
  SetTimetableCardsLockedMutation,
  SetTimetableCardsLockedMutationVariables,
} from '../../types/planung-graphql-client-defs';
import { sortRowsByBuilding } from '../../utils/roomLabel';
import { sortClasses } from '../../utils/sortHelper';
import { isDay } from '../../utils/typeguards';
import { urqlClient } from '../../utils/urqlClient';
import { TimetableCardUuid } from '../GridCardsStore';
import type { PinboardStore } from './PinboardStore';
import {
  CardStack,
  CellItem,
  HourItem,
  PlacedCardsType,
  RoomItem,
  RowItem,
  TimeGridEntryUuid,
  TimeGridEntryUuidWeekdayRowUuid,
} from './PinboardStoreTypes';
import { SubjectContainerSlot } from './SubjectContainerTypes';
import { AvailabilityCardType, CardClassDivisionGroupFormat, Day, Time } from './TimeAvailabilityStore';

/**
 * Platzhalterkarten von Epochenschienen (Fach ist ein SubjectContainer) duerfen keine eigenen
 * Raeume haben: ihre Raeume kommen in `buildCardsFromTimetableVersionData` aus den TeachingBlock-
 * Slots des Epochenplans. Wuerde man sie zurueckschreiben, entstuenden echte IN_ROOM-Beziehungen an
 * der Karte und im Gesamtraumplan (PDF) tauchten Wochenstunden auf, die es weder im Stunden- noch
 * im Epochenplan gibt. Das Backend lehnt solche Zuweisungen ebenfalls ab (setTimetableCardRooms).
 */
function canHaveRooms(card: Pick<CardType, 'isSubjectContainer'> | undefined): boolean {
  return card !== undefined && !card.isSubjectContainer;
}

export class CardStore {
  public minimalCards: { uuid: string; duration: number; subjectUuid: string; lessonUuid: string }[] = [];

  public placedCards: PlacedCardsType = new Map();
  public cardsBySlotAndResource: Map<TimeGridEntryUuidWeekdayRowUuid, Set<TimetableCardUuid>> = new Map();
  public placedCardsDay: Map<string, Day> = new Map();
  public placedCardsTimeGridEntries: Map<string, TimeGridEntryUuid[]> = new Map();
  public placedCardsSet: Set<string> = new Set();
  public freeCardsSet: Set<string> = new Set();

  public defaultCardRooms = new Map<
    string,
    {
      allUnique: RoomItem[];
      counter: number;
      lessonRoomItems: RoomItem[];
      subjectRoomItems: RoomItem[];
      teacherRoomItems: RoomItem[];
      classRoomItems: RoomItem[];
    }
  >();

  public cardRooms = new Map<TimetableCardUuid, Map<string, RoomItem>>();
  public cardClasses = new Map<TimetableCardUuid, Set<string>>();
  public cardTeacher = new Map<TimetableCardUuid, Set<string>>();
  readonly lockedCards = new Map<TimetableCardUuid, boolean>();

  public teachers: Map<string, Pick<_PeopleQuery, 'people'>['people'][0]> = new Map();
  public classes: Map<string, Pick<_ClassesQuery, 'classes'>['classes'][0]> = new Map();
  public subjects: Map<string, Pick<_SubjectsQuery, 'subjects'>['subjects'][0]> = new Map();
  public rooms: Map<string, Pick<_RoomsQuery, 'rooms'>['rooms'][0]> = new Map();

  public gridCards: Map<TimetableCardUuid, ClassGridCard[]> = new Map();
  public subjectContainerSlots: Map<TimetableCardUuid, SubjectContainerSlot[]> = new Map();

  private _cards: Map<string, CardType> = new Map();
  /** Non-reactive cache for resolved cards — avoids object allocation on every getCardByUuid call */
  readonly resolvedCardCache = new Map<string, CardType>();
  private _teachersRows: RowItem[] = [];
  private _classesRows: RowItem[] = [];
  private _roomsRows: RowItem[] = [];
  private _subjectsRows: RowItem[] = [];

  constructor(private root: PinboardStore) {
    makeAutoObservable(this, {
      gridCards: observable.shallow,
      setRoomsForCards: action.bound,
      resolvedCardCache: false, // not tracked by MobX — pure cache
    });
  }

  initCards(cards: CardType[]) {
    this.root.getTimeAvailabilityStore()?.resetPlaced();

    this._cards.clear();
    this.gridCards.clear();

    this.freeCardsSet.clear();
    this.placedCardsSet.clear();
    this.placedCardsDay.clear();
    this.placedCardsTimeGridEntries.clear();
    this.cardsBySlotAndResource.clear();
    this.root.uiStore.findingHighlights.clear();
    this.root.uiStore.clearFindingSlotHighlights();
    this.cardRooms.clear();
    this.resolvedCardCache.clear();
    this.cardClasses.clear();
    this.cardTeacher.clear();
    this.root.conflictStore.cardConflicts.clear();
    this.lockedCards.clear();
    this.defaultCardRooms.clear();
    this.root.uiStore._oldCardRooms.clear();
    this.root.uiStore._oldRoomUuidToReplace = null;
    this.root.uiStore._oldCardPlacement = null;
    this.root.uiStore.pickedCard = null;
    this.root.uiStore.rowsToHighlight = new Set();

    this.subjectContainerSlots.clear();
    this._cards = new Map(cards.map((card) => [card.uuid, card]));

    cards.forEach((card) => {
      if (card.subjectContainerSlots && card.subjectContainerSlots.length > 0) {
        this.subjectContainerSlots.set(card.uuid, card.subjectContainerSlots);
      }
    });
    this.gridCards = new Map(cards.map((card) => [card.uuid, card.classGridCards]));
    cards.forEach((card) => {
      this.minimalCards.push({
        duration: card.duration ?? 1,
        uuid: card.uuid,
        subjectUuid: card.subject?.uuid ?? '',
        lessonUuid: card.lesson?.uuid ?? '',
      });

      if (isDay(card.weekday)) {
        this.placedCardsSet.add(card.uuid);
        this.placedCardsDay.set(card.uuid, card.weekday);
      } else {
        this.freeCardsSet.add(card.uuid);
      }
      const allTimeEntries =
        card.startTimeGridEntry && card.endTimeGridEntry
          ? this.getAllHoursBetween(card.startTimeGridEntry.uuid, card.endTimeGridEntry.uuid)
          : [];

      card.classes.forEach((c) => {
        if (allTimeEntries.length > 0) {
          allTimeEntries.forEach((timeEntry) => {
            if (!isDay(card.weekday)) return;
            const current =
              this.cardsBySlotAndResource.get(`${timeEntry.value}:${card.weekday}:${c.uuid}`) ?? new Set();
            this.cardsBySlotAndResource.set(`${timeEntry.value}:${card.weekday}:${c.uuid}`, current.add(card.uuid));
          });
        }
        const current = this.cardClasses.get(card.uuid) ?? new Set();
        this.cardClasses.set(card.uuid, current.add(c.uuid));
      });

      card.teachers.forEach((t) => {
        if (allTimeEntries.length > 0) {
          allTimeEntries.forEach((timeEntry) => {
            if (!isDay(card.weekday)) return;

            const current =
              this.cardsBySlotAndResource.get(`${timeEntry.value}:${card.weekday}:${t.uuid}`) ?? new Set();
            this.cardsBySlotAndResource.set(`${timeEntry.value}:${card.weekday}:${t.uuid}`, current.add(card.uuid));
          });
        }
        const current = this.cardTeacher.get(card.uuid) ?? new Set();
        this.cardTeacher.set(card.uuid, current.add(t.uuid));
      });

      card.rooms.forEach((r) => {
        if (allTimeEntries.length > 0) {
          allTimeEntries.forEach((timeEntry) => {
            if (!isDay(card.weekday)) return;
            const current =
              this.cardsBySlotAndResource.get(`${timeEntry.value}:${card.weekday}:${r.uuid}`) ?? new Set();
            this.cardsBySlotAndResource.set(`${timeEntry.value}:${card.weekday}:${r.uuid}`, current.add(card.uuid));
          });
        }
        const current = this.cardRooms.get(card.uuid) ?? new Map();
        this.cardRooms.set(
          card.uuid,
          current.set(r.uuid, {
            label: r.name,
            value: r.uuid,
            group: '',
            inUse: false,
            buildingName: this.rooms.get(r.uuid)?.building?.name,
          }),
        );
      });

      if (card.startTimeGridEntry && card.endTimeGridEntry) {
        const allEntries = this.getAllHoursBetween(card.startTimeGridEntry.uuid, card.endTimeGridEntry.uuid);
        allEntries.forEach((entry) => {
          const cardType = {
            cardUuid: card.uuid,
            label: card.subject?.shortName ?? '',
            hasWarning: false,
            isStart: entry.value === card.startTimeGridEntry?.uuid,
            isEnd: entry.value === card.endTimeGridEntry?.uuid,
            duration: card.duration ?? 1,
            cardRows: card.badgeCardRows,
            labelColor: card.badgeCardTextColor,
          };
          if (isDay(card.weekday)) {
            const current = this.placedCards.get(`${entry.value}:${card.weekday}`);

            current?.set(card.uuid, cardType);
          }
          this.placedCardsTimeGridEntries.set(
            card.uuid,
            allEntries.map((e) => e.value),
          );
        });
      }
      if (isDay(card.weekday)) {
        this.setResourcesPlaced(card, card.startTime ?? '', card.endTime ?? '', card.weekday);
      }
      if (card.locked) {
        this.lockedCards.set(card.uuid, true);
      }
    });
    // inUse (belegte Räume) hängt an cardsBySlotAndResource ALLER Karten. Erst nach der Haupt-Schleife
    // berechnen, sonst fehlen die später eingelesenen Karten und belegte Räume werden beim ersten
    // Öffnen des Raum-Menüs nicht als belegt (rot) erkannt.
    cards.forEach((card) => this.setDefaultCardRoomsByGroup(card.uuid));
    this.root.setContext(this.root.context);
  }

  initData({
    classes,
    teachers,
    rooms,
    cards,
    subjects,
  }: {
    cards: CardType[];
    teachers: Pick<_PeopleQuery, 'people'>['people'];
    classes: Pick<_ClassesQuery, 'classes'>['classes'];
    rooms: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'];
  }) {
    this.teachers = new Map(teachers.map((teacher) => [teacher.uuid, teacher]));
    this.classes = new Map(classes.map((c) => [c.uuid, c]));
    this.rooms = new Map(rooms.map((room) => [room.uuid, room]));
    this.subjects = new Map(subjects.map((subject) => [subject.uuid, subject]));

    this.initCards(cards);

    this._teachersRows = this.teachers
      ? Array.from(this.teachers.values()).map((t) => ({ label: t.displayNameShort ?? t.shortName, value: t.uuid }))
      : [];

    this._classesRows = this.classes
      ? classes.sort(sortClasses).map((c) => ({ label: c.shortName, value: c.uuid, isFunctionClass: c.grade === null }))
      : [];

    this._roomsRows = this.rooms
      ? Array.from(this.rooms.values())
          .filter((r) => r.classroom)
          .map((r) => ({ label: r.name, value: r.uuid, subLabel: r.building?.name }))
      : [];

    this.root.setContext(this.root.context);
    this.root.conflictStore.setConflicts();
    return this;
  }

  getPlacedCardsForSlot(hourUuid: string, day: Day, rowUuid: string): CardType[] {
    const _cards = this.cardsBySlotAndResource.get(`${hourUuid}:${day}:${rowUuid}`);
    if (!_cards) return [];

    const cards: CardType[] = [];
    _cards.forEach((c) => {
      // Use getCardRaw to avoid subscribing to placedCardsDay/placedCardsTimeGridEntries/cardRooms.
      // _cards stores startTimeGridEntry/weekday directly, kept in sync by updateCardPlacement().
      const rawCard = this.getCardRaw(c);
      if (!rawCard) return;
      if (this.root.context === 'classes' && rawCard.classes.some((c) => c.uuid === rowUuid)) {
        cards.push(rawCard);
      } else if (this.root.context === 'teachers' && rawCard.teachers.some((t) => t.uuid === rowUuid)) {
        cards.push(rawCard);
      } else if (this.root.context === 'rooms') {
        cards.push(rawCard);
      }
    });

    return cards;
  }

  getPlacedCardsForCell(cell: CellItem): CardType[] {
    return this.getPlacedCardsForSlot(cell.hour.value, cell.day.value, cell.row.value);
  }

  /**
   * Reverse index of the running solver's live preview, keyed like cardsBySlotAndResource
   * (`${startSlot}:${weekday}:${resourceUuid}` → card uuids). Recomputed by MobX whenever the
   * solver's previewPlacements, the active context or the loaded cards change.
   */
  get previewCardsBySlotAndResource(): Map<TimeGridEntryUuidWeekdayRowUuid, Set<TimetableCardUuid>> {
    const index = new Map<TimeGridEntryUuidWeekdayRowUuid, Set<TimetableCardUuid>>();
    const placements = this.root.solverStore.previewPlacements;
    if (placements.size === 0) return index;

    const context = this.root.context;
    placements.forEach((placement, cardUuid) => {
      if (!placement.startTimeGridEntryUuid || !placement.weekday) return;
      const card = this.getCardRaw(cardUuid);
      if (!card) return;

      let resources: string[] = [];
      if (context === 'classes') {
        resources = card.classes.map((c) => c.uuid);
      } else if (context === 'teachers') {
        resources = card.teachers.map((t) => t.uuid);
      } else if (context === 'rooms') {
        resources = placement.roomUuids;
      }

      resources.forEach((resourceUuid) => {
        // weekday is one of the Day codes ('mon'…'fri'); guarded non-null above.
        const key =
          `${placement.startTimeGridEntryUuid}:${placement.weekday}:${resourceUuid}` as TimeGridEntryUuidWeekdayRowUuid;
        const current = index.get(key) ?? new Set<TimetableCardUuid>();
        index.set(key, current.add(cardUuid));
      });
    });

    return index;
  }

  /** Cards the running solver proposes for this cell (live preview ghost cards). */
  getPreviewCardsForCell(cell: CellItem): CardType[] {
    const set = this.previewCardsBySlotAndResource.get(`${cell.hour.value}:${cell.day.value}:${cell.row.value}`);
    if (!set) return [];
    const cards: CardType[] = [];
    set.forEach((uuid) => {
      const card = this.getCardRaw(uuid);
      if (card) cards.push(card);
    });
    return cards;
  }

  /** Update placement fields on the raw card in _cards so getCardRaw returns current data */
  updateCardPlacement(
    cardUuid: string,
    startTimeGridEntry: string | null,
    endTimeGridEntry: string | null,
    weekday: string | undefined,
  ) {
    const card = this._cards.get(cardUuid);
    if (!card) return;
    card.startTimeGridEntry = startTimeGridEntry ? { uuid: startTimeGridEntry } : null;
    card.endTimeGridEntry = endTimeGridEntry ? { uuid: endTimeGridEntry } : null;
    card.weekday = weekday;
  }

  setResourcesPlaced(
    card: Pick<CardType, 'uuid' | 'teachers' | 'classes' | 'rooms' | 'lessonClasses' | 'subject'>,
    start: string,
    end: string,
    day: Day,
  ) {
    if (start && end && day) {
      const classDivisionGroups: CardClassDivisionGroupFormat[] = [];
      card.lessonClasses.forEach((lc) => {
        if (lc.groups.length !== 0) {
          lc?.groups.forEach((g) => {
            if (lc.usedDivision) {
              classDivisionGroups.push(
                `${card.uuid}:${lc.class.uuid}:${lc.usedDivision?.uuid ? lc.usedDivision.uuid : ''}:${g.uuid}`,
              );
            }
          });
        } else {
          classDivisionGroups.push(`${card.uuid}:${lc.class.uuid}:noDivision:noGroup`);
        }
      });
      this.root.getTimeAvailabilityStore()?.setCardsResourcesPlaced(
        [
          {
            uuid: card.uuid,
            teachers: card.teachers.map((t) => t.uuid),
            classes: card.classes.map((c) => c.uuid),
            classDivisionGroups: classDivisionGroups,
            rooms: card.rooms.map((r) => r.uuid),
            subject: card.subject?.uuid ?? '',
          },
        ],
        {
          start: dayjs(start, 'HH:mm').format('HH:mm') as Time,
          end: dayjs(end, 'HH:mm').format('HH:mm') as Time,
          day: day,
        },
      );
    }
  }

  getRows() {
    if (this.root.context === 'subjects') {
      return this._subjectsRows;
    }
    if (this.root.context === 'rooms') {
      return this.root.groupRoomsByBuilding ? sortRowsByBuilding(this._roomsRows) : this._roomsRows;
    }
    if (this.root.context === 'teachers') {
      return this._teachersRows;
    }
    return this._classesRows;
  }

  getCurrentVersion() {
    return this.root.currentVersion;
  }

  /** Raw card from _cards — no rooms/weekday resolution, no object allocation */
  getCardRaw(uuid: string): CardType | undefined {
    return this._cards.get(uuid);
  }

  getCardByUuid(uuid: string): CardType | undefined {
    const cached = this.resolvedCardCache.get(uuid);
    if (cached) return cached;

    const card = this._cards.get(uuid);
    if (!card) return undefined;

    const weekday = this.placedCardsDay.get(uuid);
    const timeGridEntry = this.placedCardsTimeGridEntries.get(uuid);
    const resolved: CardType = {
      ...card,
      rooms: Array.from(this.cardRooms.get(uuid)?.values() ?? []).map((r) => ({ uuid: r.value, name: r.label })),
      weekday: weekday,
      startTimeGridEntry: timeGridEntry ? { uuid: timeGridEntry[0] } : null,
      endTimeGridEntry: timeGridEntry ? { uuid: timeGridEntry[timeGridEntry.length - 1] } : null,
    };
    this.resolvedCardCache.set(uuid, resolved);
    return resolved;
  }

  /** Invalidate resolved card cache for a specific card or all cards */
  invalidateResolvedCard(uuid?: string) {
    if (uuid) {
      this.resolvedCardCache.delete(uuid);
    } else {
      this.resolvedCardCache.clear();
    }
  }

  getCardsByLessonUuid(uuid: string): CardType[] | undefined {
    return Array.from(this._cards.values()).filter((card) => card.lesson?.uuid === uuid);
  }

  getAvailableCardForCardUuid(cardUuid: string): AvailabilityCardType {
    const card = this.getCardByUuid(cardUuid);
    const classDivisionGroups: CardClassDivisionGroupFormat[] = [];
    card?.lessonClasses.forEach((lc) => {
      if (lc.groups.length !== 0) {
        lc?.groups.forEach((g) => {
          classDivisionGroups.push(
            `${cardUuid}:${lc.class.uuid}:${lc.usedDivision?.uuid ? lc.usedDivision.uuid : ''}:${g.uuid}`,
          );
        });
      } else {
        classDivisionGroups.push(`${cardUuid}:${lc.class.uuid}:noDivision:noGroup`);
      }
    });

    const rooms = this.cardRooms.get(cardUuid);

    return {
      uuid: card?.uuid ?? '',
      rooms: rooms ? Array.from(rooms.values()).map((r) => r.value) : [],
      classes: card?.classes.map((c) => c?.uuid ?? '') ?? [],
      teachers: card?.teachers?.map((t) => t.uuid) ?? [],
      classDivisionGroups: classDivisionGroups,
      subject: card?.subject?.uuid ?? '',
    };
  }

  _removeCardFromPlacedCards(cardUuid: string) {
    const day = this.placedCardsDay.get(cardUuid);
    const cells = this.placedCardsTimeGridEntries.get(cardUuid);
    if (day) {
      cells?.forEach((_cell) => {
        this.placedCards.get(`${_cell}:${day}`)?.delete(cardUuid);
        this.cardTeacher.get(cardUuid)?.forEach((t) => {
          this.cardsBySlotAndResource.get(`${_cell}:${day}:${t}`)?.delete(cardUuid);
        });
        this.cardRooms.get(cardUuid)?.forEach((r) => {
          this.cardsBySlotAndResource.get(`${_cell}:${day}:${r.value}`)?.delete(cardUuid);
        });
        this.cardClasses.get(cardUuid)?.forEach((c) => {
          this.cardsBySlotAndResource.get(`${_cell}:${day}:${c}`)?.delete(cardUuid);
        });
      });
    }
    this._removeCardResources(cardUuid);
    this.placedCardsSet.delete(cardUuid);
    this.placedCardsDay.delete(cardUuid);
    this.placedCardsTimeGridEntries.delete(cardUuid);
    this.updateCardPlacement(cardUuid, null, null, undefined);
    this.invalidateResolvedCard(cardUuid);
  }

  getAllHoursBetween(start: string, end: string): HourItem[] {
    const startIndex = this.root.hours.findIndex((h) => h.value === start);
    const endIndex = this.root.hours.findIndex((h) => h.value === end);
    if (startIndex === -1) return [];
    return endIndex === -1 ? [this.root.hours[startIndex]] : this.root.hours.slice(startIndex, endIndex + 1);
  }

  getFollowers(hourUuid: string, duration: number): HourItem[] | null {
    const startIndex = this.root.hours.findIndex((item) => item.value === hourUuid);
    if (duration === 1) {
      return [this.root.hours[startIndex]];
    }
    if (startIndex === -1 || startIndex + duration - 1 >= this.root.hours.length) {
      return null;
    }
    return this.root.hours
      .filter((i, index) => !i.isPause || index <= startIndex)
      .slice(startIndex + 1, startIndex + 1 + duration - 1)
      .map((item) => item);
  }

  setDefaultCardRoomsByGroup(cardUuid: string) {
    const card = this.getCardByUuid(cardUuid);
    if (card) {
      const day = this.placedCardsDay.get(card.uuid);
      const timeGridEntries = this.placedCardsTimeGridEntries.get(card.uuid);

      // Raum-Hierarchie: Raumvorrat (lesson) > Fachraum (subject) > Lehrerraum (teacher) > Klassenraum (class).
      // Quellen in genau dieser Reihenfolge einsammeln und beim Dedup FIRST-WINS halten (`has`-Guard), damit ein
      // Raum, der in mehreren Quellen vorkommt, der höchstrangigen Gruppe zugeordnet bleibt — „Raumvorrat schlägt
      // Klassen-/Lehrer-/Fachraum" (Issue). (Vorher war es `Map.set` last-wins ⇒ die Reihenfolge war invertiert.)
      const uniqueRooms = new Map();
      card.lesson?.roomSupplyConnection.edges.forEach(({ node }) => {
        const room = toJS(this.rooms.get(node.uuid));
        if (room && !uniqueRooms.has(room.uuid)) {
          const inUse =
            day && timeGridEntries
              ? timeGridEntries.some((tge) => {
                  const match = this.cardsBySlotAndResource.get(`${tge}:${day}:${room.uuid}`);
                  return match && match.size > 0;
                })
              : false;
          uniqueRooms.set(room.uuid, {
            roomItem: {
              inUse: inUse,
              group: 'lesson',
              label: room.name,
              value: room.uuid,
              buildingName: room.building?.name,
            },
            group: 'lesson',
          });
        }
      });

      if (card.subject?.__typename === 'SubjectItem') {
        card.subject.defaultRooms.forEach((defaultRoom) => {
          const room = toJS(this.rooms.get(defaultRoom.uuid));

          if (room && !uniqueRooms.has(room.uuid)) {
            const inUse =
              day && timeGridEntries
                ? timeGridEntries.some((tge) => {
                    const match = this.cardsBySlotAndResource.get(`${tge}:${day}:${room.uuid}`);
                    return match && match.size > 0;
                  })
                : false;
            uniqueRooms.set(room.uuid, {
              roomItem: {
                inUse: inUse,
                group: 'subject',
                label: room.name,
                value: room.uuid,
                buildingName: room.building?.name,
              },
              group: 'subject',
            });
          }
        });
      }

      card.teachers.forEach((t) => {
        const room = toJS(this.rooms.get(t.defaultRoomUuid ?? ''));
        if (room && !uniqueRooms.has(room.uuid)) {
          const inUse =
            day && timeGridEntries
              ? timeGridEntries.some((tge) => {
                  const match = this.cardsBySlotAndResource.get(`${tge}:${day}:${room.uuid}`);
                  return match && match.size > 0;
                })
              : false;
          uniqueRooms.set(room.uuid, {
            roomItem: {
              inUse: inUse,
              group: 'teacher',
              label: room.name,
              value: room.uuid,
              buildingName: room.building?.name,
            },
            group: 'teacher',
          });
        }
      });

      card.classes.forEach((c) => {
        const room = toJS(this.rooms.get(c.defaultRoomUuid ?? ''));
        if (room && !uniqueRooms.has(room.uuid)) {
          const inUse =
            day && timeGridEntries
              ? timeGridEntries.some((tge) => {
                  const match = this.cardsBySlotAndResource.get(`${tge}:${day}:${room.uuid}`);
                  return match && match.size > 0;
                })
              : false;
          uniqueRooms.set(room.uuid, {
            roomItem: {
              inUse: inUse,
              group: 'class',
              label: room.name,
              value: room.uuid,
              buildingName: room.building?.name,
            },
            group: 'class',
          });
        }
      });

      const lessonDefaultRooms = Array.from(uniqueRooms.values())
        .filter((r) => r.group === 'lesson')
        .map((r) => r.roomItem);
      const subjectDefaultRooms = Array.from(uniqueRooms.values())
        .filter((r) => r.group === 'subject')
        .map((r) => r.roomItem);
      const teacherDefaultRooms = Array.from(uniqueRooms.values())
        .filter((r) => r.group === 'teacher')
        .map((r) => r.roomItem);
      const classDefaultRooms = Array.from(uniqueRooms.values())
        .filter((r) => r.group === 'class')
        .map((r) => r.roomItem);

      this.defaultCardRooms.set(card.uuid, {
        allUnique: Array.from(uniqueRooms.values()).map((r) => r.roomItem),
        lessonRoomItems: lessonDefaultRooms,
        subjectRoomItems: subjectDefaultRooms,
        teacherRoomItems: teacherDefaultRooms,
        classRoomItems: classDefaultRooms,
        counter: uniqueRooms.size,
      });
    }
  }

  setRoomsForCards(cards: CardType[], roomsToSet: RoomItem[], keepCurrent: boolean = false) {
    if (!this.root.isEditable) return;
    const promises: Promise<unknown>[] = [];
    cards.filter(canHaveRooms).forEach((card) => {
      this._removeCardResources(card.uuid);
      const newCardRooms = this.cardRooms.get(card.uuid) ?? new Map<string, RoomItem>();
      const day = this.placedCardsDay.get(card.uuid);
      const timeGridEntries = this.placedCardsTimeGridEntries.get(card.uuid);

      if (!keepCurrent) {
        if (day && timeGridEntries) {
          newCardRooms.forEach((roomItem) => {
            timeGridEntries.forEach((tge) => {
              const current = this.cardsBySlotAndResource.get(`${tge}:${day}:${roomItem.value}`) ?? new Set();
              current.delete(card.uuid);
            });
          });
        }
        newCardRooms.clear();
      }

      roomsToSet.forEach((room) => {
        newCardRooms.set(room.value, room);
      });

      const newCardRoomsArray = Array.from(newCardRooms.values()).map((r) => r.value);

      promises.push(
        urqlClient.mutation(SetCardRoomsDocument, { cardUuid: card.uuid, roomUuids: newCardRoomsArray }).toPromise(),
      );
      this.cardRooms.set(card.uuid, newCardRooms);
      this.invalidateResolvedCard(card.uuid);
      if (day && timeGridEntries) {
        newCardRoomsArray.forEach((roomUuid) => {
          timeGridEntries.forEach((tge) => {
            const current = this.cardsBySlotAndResource.get(`${tge}:${day}:${roomUuid}`) ?? new Set();
            this.cardsBySlotAndResource.set(`${tge}:${day}:${roomUuid}`, current.add(card.uuid));
            const placedCards = this.placedCards.get(`${tge}:${day}`);
            placedCards?.forEach((c) => {
              this.setDefaultCardRoomsByGroup(c.cardUuid);
            });
          });
        });
      }
      this._setNewCardResources(card.uuid);
    });

    void Promise.all(promises);
    const affected = new Set(cards.map((c) => c.uuid));
    cards.forEach((c) => this.root.conflictStore._getAffectedCardsForCard(c.uuid).forEach((u) => affected.add(u)));
    this.root.conflictStore.updateConflictsForCards(affected);
  }

  removeRoomsFromCards(
    cards: { uuid: string }[],
    roomsToRemove: (Partial<RoomItem> & { value: string })[],
    removeAll: boolean = false,
  ) {
    if (!this.root.isEditable) return;

    cards
      .filter((card) => canHaveRooms(this.getCardByUuid(card.uuid)))
      .forEach((card) => {
        this._removeCardResources(card.uuid);

        const newCardRooms = this.cardRooms.get(card.uuid) ?? new Map<string, RoomItem>();

        const day = this.placedCardsDay.get(card.uuid);
        const timeGridEntries = this.placedCardsTimeGridEntries.get(card.uuid);

        if (removeAll) {
          newCardRooms.clear();
        } else {
          roomsToRemove.forEach((room) => {
            newCardRooms.delete(room.value);
          });
        }

        const newCardRoomsArray = Array.from(newCardRooms.values()).map((r) => r.value);

        void urqlClient
          .mutation(SetCardRoomsDocument, { cardUuid: card.uuid, roomUuids: newCardRoomsArray })
          .toPromise();

        this.cardRooms.set(card.uuid, newCardRooms);
        this.invalidateResolvedCard(card.uuid);
        this._setNewCardResources(card.uuid);

        if (day && timeGridEntries) {
          roomsToRemove.forEach((room) => {
            const current = this.cardsBySlotAndResource.get(`${timeGridEntries[0]}:${day}:${room.value}`) ?? new Set();
            current.delete(card.uuid);
          });
          const placedCards = this.placedCards.get(`${timeGridEntries[0]}:${day}`);
          placedCards?.forEach((c) => {
            this.setDefaultCardRoomsByGroup(c.cardUuid);
          });
        }
      });
    const affectedByRemove = new Set(cards.map((c) => c.uuid));
    cards.forEach((c) =>
      this.root.conflictStore._getAffectedCardsForCard(c.uuid).forEach((u) => affectedByRemove.add(u)),
    );
    this.root.conflictStore.updateConflictsForCards(affectedByRemove);
  }

  _removeCardResources(cardUuid: string) {
    const placedCardDay = this.placedCardsDay.get(cardUuid);
    const placedCardTimeGridEntries = this.placedCardsTimeGridEntries.get(cardUuid);
    const start = this.root.timeGridEntries?.find((e) => e.uuid === placedCardTimeGridEntries?.[0])?.start ?? null;
    const end =
      this.root.timeGridEntries?.find(
        (e) => e.uuid === placedCardTimeGridEntries?.[placedCardTimeGridEntries.length - 1],
      )?.end ?? null;
    const availableCard = this.getAvailableCardForCardUuid(cardUuid);
    if (placedCardDay && start && end) {
      this.root.getTimeAvailabilityStore()?.removeCardsResourcesPlaced([availableCard], {
        start: dayjs(start).format('HH:mm') as Time,
        end: dayjs(end).format('HH:mm') as Time,
        day: placedCardDay,
      });
    }
  }

  _setNewCardResources(cardUuid: string) {
    const placedCardDay = this.placedCardsDay.get(cardUuid);
    const placedCardTimeGridEntries = this.placedCardsTimeGridEntries.get(cardUuid);
    const start = this.root.timeGridEntries?.find((e) => e.uuid === placedCardTimeGridEntries?.[0])?.start ?? null;
    const end =
      this.root.timeGridEntries?.find(
        (e) => e.uuid === placedCardTimeGridEntries?.[placedCardTimeGridEntries.length - 1],
      )?.end ?? null;
    const availableCard = this.getAvailableCardForCardUuid(cardUuid);
    if (placedCardDay && start && end) {
      this.root.getTimeAvailabilityStore()?.setCardsResourcesPlaced([availableCard], {
        start: dayjs(start).format('HH:mm') as Time,
        end: dayjs(end).format('HH:mm') as Time,
        day: placedCardDay,
      });
    }
  }

  lockCard(cardUuid: string) {
    if (!this.root.isEditable) return;

    void urqlClient
      .mutation<SetTimetableCardsLockedMutation, SetTimetableCardsLockedMutationVariables>(
        SetTimetableCardsLockedDocument,
        { cardUuids: [cardUuid], locked: true },
      )
      .toPromise();
    this.lockedCards.set(cardUuid, true);
  }

  unlockCard(cardUuid: string) {
    if (!this.root.isEditable) return;

    void urqlClient
      .mutation<SetTimetableCardsLockedMutation, SetTimetableCardsLockedMutationVariables>(
        SetTimetableCardsLockedDocument,
        { cardUuids: [cardUuid], locked: false },
      )
      .toPromise();
    this.lockedCards.delete(cardUuid);
  }

  getCardsAtDay(day: { label: string; value: Day }): CardType[] {
    const cards: CardType[] = [];
    this.root.timeGridEntries?.forEach((entry) => {
      this.placedCards.get(`${entry.uuid}:${day.value}`)?.forEach((card) => {
        const c = this.getCardByUuid(card.cardUuid);
        if (c) {
          cards.push({
            ...c,
            weekday: day.value,
            rooms: this.cardRooms.get(c.uuid)
              ? Array.from(this.cardRooms.get(c.uuid)!.values()).map((r) => ({ uuid: r.value, name: r.label }))
              : [],
          });
        }
      });
    });
    return cards;
  }

  getOverlappingCards(cardUuid: string): CardType[] {
    const cards = new Map();
    if (this.root.context === 'classes') {
      const timeGridEntries = this.placedCardsTimeGridEntries.get(cardUuid);
      const weekday = this.placedCardsDay.get(cardUuid);
      const cardClasses = this.cardClasses.get(cardUuid);
      if (weekday) {
        timeGridEntries?.forEach((tge) => {
          if (this.placedCards.get(`${tge}:${weekday}`)) {
            this.placedCards.get(`${tge}:${weekday}`)?.forEach((card) => {
              const c = this.getCardByUuid(card.cardUuid);
              if (
                c?.classes.some((classItem) => {
                  return cardClasses ? cardClasses.has(classItem.uuid) : false;
                })
              ) {
                cards.set(card.cardUuid, {
                  ...c,
                });
              }
            });
          }
        });
      }
    }
    return cards.size > 1 ? Array.from(cards.values()) : [];
  }

  /** Perioden (TimeGridEntry-Uuids), die eine Karte belegt – bei Mehrfachstunden mehrere. */
  private _periodsOfCard(card: CardType): string[] {
    const start = card.startTimeGridEntry?.uuid;
    if (!start) return [];
    const end = card.endTimeGridEntry?.uuid ?? start;
    return this.getAllHoursBetween(start, end)
      .filter((h) => !!h)
      .map((h) => h.value);
  }

  /**
   * Alle Karten, die mit `cards` über gemeinsame Perioden zusammenhängen (transitive Hülle in
   * dieser row/day). Nötig, weil die Spurvergabe in jeder Cell dasselbe Ergebnis liefern muss:
   * eine überspannende Karte wird in ihrer Startcell gerendert, ihre Nachbarn in anderen Cells.
   * Rechneten die Cells über unterschiedliche Kartenmengen, bekäme dieselbe Karte je nach Cell
   * eine andere Spur – und die Karten würden übereinander liegen.
   */
  private _coexistGroup(cards: CardType[], day: Day, rowUuid: string): CardType[] {
    const group = new Map<string, CardType>(cards.map((c) => [c.uuid, c]));
    const queue = [...cards];
    const seenPeriods = new Set<string>();

    while (queue.length > 0) {
      const card = queue.pop();
      if (!card) continue;
      this._periodsOfCard(card).forEach((period) => {
        if (seenPeriods.has(period)) return;
        seenPeriods.add(period);
        this.getPlacedCardsForSlot(period, day, rowUuid).forEach((neighbour) => {
          if (group.has(neighbour.uuid)) return;
          group.set(neighbour.uuid, neighbour);
          queue.push(neighbour);
        });
      });
    }

    return [...group.values()];
  }

  /**
   * Layout koexistierender Karten (elective/attendance dürfen sich eine Cell teilen).
   *  - coexisting: mehr als eine überlappende Karte und alle koexistierbar.
   *  - coexistCards: die Karten dieser Cell (gerendert wird nur, wer hier startet).
   *  - coexistLayout: je Karte die vertikale Spur (`track`) und die Teilung (`divisions`).
   *
   * Die Teilung wird PRO KARTE aus ihrer engsten Periode bestimmt, nicht aus der Gesamtzahl der
   * Karten: liegen über eine Dreifachstunde verteilt zwei Gegenkarten in verschiedenen Perioden,
   * teilen sich nirgends mehr als zwei Karten eine Periode – also 1/2 Höhe, nicht 1/3.
   * Die Spuren kommen aus einer Greedy-Färbung (jede Karte bekommt die kleinste Spur, die in
   * keiner ihrer Perioden belegt ist), damit Karten in verschiedenen Perioden dieselbe Spur
   * nachnutzen können und die überspannende Karte bündig durchläuft.
   */
  getCoexistLayout(
    cards: CardType[],
    day: Day,
    rowUuid: string,
  ): {
    coexisting: boolean;
    coexistCards: CardType[];
    coexistLayout: Map<string, { track: number; divisions: number }>;
  } {
    const empty = new Map<string, { track: number; divisions: number }>();
    // Billiger Ausstieg für den Normalfall (nicht koexistierbare Karten): die Karten dieser Cell
    // sind Teilmenge der Gruppe, eine davon reicht also, um Koexistenz auszuschließen – dann muss
    // die Gruppe gar nicht erst gesucht werden.
    if (cards.length === 0 || !cards.every(isCoexistableCard)) {
      return { coexisting: false, coexistCards: cards, coexistLayout: empty };
    }

    const group = this._coexistGroup(cards, day, rowUuid);
    const coexisting = group.length > 1 && group.every(isCoexistableCard);
    if (!coexisting) {
      return { coexisting: false, coexistCards: cards, coexistLayout: empty };
    }

    // Deterministische Reihenfolge: die Greedy-Färbung muss in jeder Cell identisch laufen.
    const hourIndex = (uuid?: string) => this.root.hours.findIndex((h) => h.value === uuid);
    const ordered = [...group].sort(
      (a, b) =>
        hourIndex(a.startTimeGridEntry?.uuid) - hourIndex(b.startTimeGridEntry?.uuid) ||
        (b.duration ?? 1) - (a.duration ?? 1) ||
        a.uuid.localeCompare(b.uuid),
    );

    const cardPeriods = new Map<string, string[]>();
    const tracksByPeriod = new Map<string, Set<number>>();
    const trackByCard = new Map<string, number>();

    ordered.forEach((card) => {
      const periods = this._periodsOfCard(card);
      cardPeriods.set(card.uuid, periods);

      let track = 0;
      while (periods.some((p) => tracksByPeriod.get(p)?.has(track))) track++;

      trackByCard.set(card.uuid, track);
      periods.forEach((p) => {
        const taken = tracksByPeriod.get(p) ?? new Set<number>();
        tracksByPeriod.set(p, taken.add(track));
      });
    });

    const coexistLayout = new Map<string, { track: number; divisions: number }>();
    ordered.forEach((card) => {
      const track = trackByCard.get(card.uuid) ?? 0;
      const divisions = (cardPeriods.get(card.uuid) ?? []).reduce(
        (max, p) => Math.max(max, tracksByPeriod.get(p)?.size ?? 1),
        // track + 1 als Untergrenze: die Karte muss in ihre eigene Spur passen.
        track + 1,
      );
      coexistLayout.set(card.uuid, { track, divisions });
    });

    return { coexisting: true, coexistCards: cards, coexistLayout };
  }

  canBeDropped(cardUuid: string, cardDuration: number, hourUuid: string, day: Day): boolean {
    if (this.root.context === 'rooms') {
      const weekday = this.placedCardsDay.get(cardUuid);
      const timeGridEntries = this.placedCardsTimeGridEntries.get(cardUuid);

      if (!timeGridEntries || !weekday) return false;
      return timeGridEntries.some((entry) => {
        return entry === hourUuid && weekday === day;
      });
    }
    const followers = this.getFollowers(hourUuid, cardDuration ?? 1);
    return followers !== null;
  }

  getCardsStack(): CardStack {
    const cards: CardStack = new Map();
    const validUuids = new Set<string>();
    this.root.selectedRows.forEach((row) => {
      this._cards.forEach((c) => {
        const _c = this.getCardByUuid(c.uuid);
        if ((_c && _c.classes.some((c) => c.uuid === row.value)) || _c?.teachers.some((t) => t.uuid === row.value)) {
          validUuids.add(_c.uuid);
        }
      });
    });
    this._cards.forEach((c) => {
      const _c = this.getCardByUuid(c.uuid);
      if (!_c) return;
      if (this.root.context === 'rooms') {
        const rooms = this.cardRooms.get(_c.uuid);
        if (this.placedCardsDay.has(_c.uuid) && (!rooms || rooms?.size === 0)) {
          if (this.root.selectedColumns.size > 0 && _c.startTimeGridEntry?.uuid && isDay(_c.weekday)) {
            if (this.root.selectedColumns.has(`${_c.startTimeGridEntry?.uuid}:${_c.weekday}`)) {
              cards.set(`${_c.uuid}:${_c.uuid}`, [_c]);
            }
          } else {
            cards.set(`${_c.uuid}:${_c.uuid}`, [_c]);
          }
        }
      } else {
        if (this.freeCardsSet.has(_c.uuid)) {
          const lessonUuid = _c.lesson?.uuid ?? '';
          const duration = `${_c.duration ?? 1}`;
          const currentCards = cards.get(`${lessonUuid}:${duration}`);
          if (this.root.selectedRows.length > 0) {
            if (validUuids.has(_c.uuid)) {
              cards.set(`${lessonUuid}:${duration}`, currentCards ? [...currentCards, _c] : [_c]);
            }
          } else {
            cards.set(`${lessonUuid}:${duration}`, currentCards ? [...currentCards, _c] : [_c]);
          }
        }
      }
    });

    return cards;
  }

  getNameForUuid(uuid: string, type: string): string {
    switch (type) {
      case 'subject':
        return this.subjects.get(uuid)?.name ?? '';
      case 'teacher':
        return this.teachers.get(uuid)?.displayNameShort ?? '';
      case 'class':
        return this.classes.get(uuid)?.name ?? '';
      case 'room':
        return this.rooms.get(uuid)?.name ?? '';
      case 'divisionGroup':
        return '';
      default:
        return '';
    }
  }

  addClasses(classes: Pick<_ClassesQuery, 'classes'>['classes']) {
    classes.forEach((r) => {
      this.classes.set(r.uuid, r);
    });
    const classArray = Array.from(this.classes.values());
    this._classesRows = classArray
      .sort(sortClasses)
      .map((c) => ({ label: c.shortName, value: c.uuid, isFunctionClass: c.grade === null }));

    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  removeClasses(uuids: string[]) {
    uuids.forEach((uuid) => {
      this.classes.delete(uuid);
    });
    const classArray = Array.from(this.classes.values());
    this._classesRows = classArray
      .sort(sortClasses)
      .map((c) => ({ label: c.shortName, value: c.uuid, isFunctionClass: c.grade === null }));

    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  getClassRows() {
    return this._classesRows;
  }

  addSubjects(subjects: Pick<_SubjectsQuery, 'subjects'>['subjects']) {
    subjects.forEach((r) => {
      this.subjects.set(r.uuid, r);
    });
  }

  removeSubjects(uuids: string[]) {
    uuids.forEach((uuid) => {
      this.subjects.delete(uuid);
    });
  }

  addTeachers(teachers: Pick<_PeopleQuery, 'people'>['people']) {
    teachers.forEach((r) => {
      this.teachers.set(r.uuid, r);
    });
    const teacherArray = Array.from(this.teachers.values());
    this._teachersRows = teacherArray
      .sort((a, b) => a.lastName.localeCompare(b.lastName))
      .map((t) => ({ label: t.displayNameShort ?? t.shortName, value: t.uuid }));
    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  removeTeachers(uuids: string[]) {
    uuids.forEach((uuid) => {
      this.teachers.delete(uuid);
    });
    const teacherArray = Array.from(this.teachers.values());
    this._teachersRows = teacherArray
      .sort((a, b) => a.lastName.localeCompare(b.lastName))
      .map((t) => ({ label: t.displayNameShort ?? t.shortName, value: t.uuid }));
    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  addRooms(rooms: Pick<_RoomsQuery, 'rooms'>['rooms']) {
    rooms.forEach((r) => {
      this.rooms.set(r.uuid, r);
    });
    const roomArray = Array.from(this.rooms.values());
    this._roomsRows = roomArray
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({ label: r.name, value: r.uuid, subLabel: r.building?.name }));
    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  removeRooms(uuids: string[]) {
    uuids.forEach((uuid) => {
      this.rooms.delete(uuid);
    });
    const roomArray = Array.from(this.rooms.values());
    this._roomsRows = roomArray
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({ label: r.name, value: r.uuid, subLabel: r.building?.name }));
    // Kontext beibehalten: gewollt sind hier nur das Zuruecksetzen der Auswahl und setConflicts().
    this.root.setContext(this.root.context);
  }

  /**
   * Number of cards a solver run would actually (re)place for the given class scope.
   * This is what the wizard footer must show — not the count of free/unplaced cards.
   * Locked cards are never moved by the solver, so they never count. With
   * keepExistingPositions only unplaced cards are touched; otherwise every non-locked
   * in-scope card is fair game.
   */
  countSolverChangeableCards(classUuids: string[]): number {
    let count = 0;

    this._cards.forEach((card) => {
      const isInSelectedClasses = card.classes.some((c) => classUuids.includes(c.uuid));
      if (!isInSelectedClasses) {
        return;
      }
      if (this.lockedCards.get(card.uuid) === true) {
        return;
      }
      if (this.root.solverStore.generatorOptions.keepExistingPositions) {
        if (!this.placedCardsDay.has(card.uuid)) {
          count++;
        }
      } else {
        count++;
      }
    });

    return count;
  }

  hasCardsToPlace(classUuids: string[]): boolean {
    return this.countSolverChangeableCards(classUuids) > 0;
  }

  /**
   * Number of placed, non-locked cards in scope — the prerequisite for OPTIMIZE and ASSIGN_ROOMS,
   * which act on the existing placement (locked cards stay fixed, so they don't count). Unlike
   * countSolverChangeableCards this counts PLACED cards regardless of keepExistingPositions.
   */
  countMovablePlacedCards(classUuids: string[]): number {
    let count = 0;
    this._cards.forEach((card) => {
      if (!card.classes.some((c) => classUuids.includes(c.uuid))) {
        return;
      }
      if (this.lockedCards.get(card.uuid) === true) {
        return;
      }
      if (this.placedCardsDay.has(card.uuid)) {
        count++;
      }
    });
    return count;
  }

  hasPlacedCards(classUuids: string[]): boolean {
    return this.countMovablePlacedCards(classUuids) > 0;
  }
}
